/* @jsx h */
import { LemmonModal } from "@lemmonhq/uikit";
import { defineComponent, onMounted, onUnmounted, ref, h, watch } from "vue";
import { api } from "@app/data/api";
import { QRCodeComponent } from "@lemmonhq/uikit";
import { formatPrice } from "@lemmonhq/orders";
import { useCheckout } from "../../providers";
import { CheckoutData } from "@lemmonhq/types";
import { useAuth } from "@app/vue/providers/Auth";

interface OnlinePaymentModalV3Props {
  onClose: () => void;
  onConfirm: (
    amountReceived: number,
    paymentId: number,
    tipAmount?: number,
  ) => void;
  totalAmount: number;
  checkoutId: string;
}

type TransactionStatus =
  | "initializing"
  | "payment_created"
  | "waiting_for_payment"
  | "payment_confirmed"
  | "processing_payment"
  | "completed"
  | "failed"
  | "offline";

export const OnlinePaymentModalV3 = defineComponent<OnlinePaymentModalV3Props>({
  name: "OnlinePaymentModalV3",
  props: ["onClose", "onConfirm", "totalAmount", "checkoutId"],
  setup(props) {
    const checkout = useCheckout();
    const auth = useAuth();
    const invocationId = ref<string | null>(null);
    const paymentUrl = ref<string | null>(null);
    const paymentId = ref<number | null>(null);
    const workflowStatus = ref<TransactionStatus>("initializing");
    const isOnline = ref(navigator.onLine);
    const pollingInterval = ref<NodeJS.Timeout | null>(null);
    const pollingInProgress = ref(false);
    const activePollController = ref<AbortController | null>(null);

    const POLL_INTERVAL = 3000;
    const MAX_POLL_TIME = 3 * 60 * 1000; // 3 minutes
    const startTime = ref(Date.now());

    // Derived state - prevents race conditions by computing from workflow status
    const displayStatus = ref<
      | "initializing"
      | "waiting"
      | "processing"
      | "success"
      | "error"
      | "offline"
    >("initializing");
    const statusMessage = ref("Initializing online payment...");

    // Derive display state from workflow status - prevents race conditions
    watch(
      [workflowStatus, isOnline, paymentUrl],
      ([status, online, url]) => {
        if (!online && status !== "completed" && status !== "failed") {
          displayStatus.value = "offline";
          statusMessage.value =
            "No internet connection. Waiting for connection...";
          return;
        }

        switch (status) {
          case "initializing":
            displayStatus.value = "initializing";
            statusMessage.value = "Creating payment...";
            break;
          case "payment_created":
            displayStatus.value = url ? "waiting" : "processing";
            statusMessage.value = url
              ? "Scan QR code to complete payment"
              : "Generating payment link...";
            break;
          case "waiting_for_payment":
            displayStatus.value = "waiting";
            statusMessage.value = "Scan QR code to complete payment";
            break;
          case "payment_confirmed":
            displayStatus.value = "processing";
            statusMessage.value = "Payment confirmed, processing...";
            break;
          case "processing_payment":
            displayStatus.value = "processing";
            statusMessage.value = "Processing payment...";
            break;
          case "completed":
            displayStatus.value = "success";
            statusMessage.value = "Payment successful!";
            break;
          case "failed":
            displayStatus.value = "error";
            statusMessage.value = "Payment failed";
            break;
          case "offline":
            displayStatus.value = "offline";
            statusMessage.value =
              "No internet connection. Waiting for connection...";
            break;
        }
      },
      { immediate: true },
    );

    // Network status detection
    const handleOnline = () => {
      isOnline.value = true;
    };
    const handleOffline = () => {
      isOnline.value = false;
    };

    onMounted(() => {
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
      startService();
    });

    onUnmounted(() => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      stopPolling();
    });

    // Watch for network changes - restart polling when coming back online
    watch(isOnline, (online) => {
      if (online && invocationId.value) {
        const finalStates: TransactionStatus[] = ["completed", "failed"];
        if (!finalStates.includes(workflowStatus.value)) {
          startPolling();
        }
      } else if (!online) {
        stopPolling();
      }
    });

    const startService = async (): Promise<void> => {
      try {
        workflowStatus.value = "initializing";

        if (!checkout.checkout.value) {
          throw new Error("No checkout data available");
        }

        // Prepare checkout data with payment info
        const checkoutData: CheckoutData = {
          ...checkout.checkout.value,
          payment: {
            amountRcvd: props.totalAmount,
            paymentType: "online",
          },
        };
        checkout.setCheckout(checkoutData);

        const { invocationId: invId } = await api.invokeOnlinePaymentService(
          props.totalAmount,
          props.checkoutId,
          checkoutData,
        );

        invocationId.value = invId;
        startTime.value = Date.now();

        // Start polling for service status
        startPolling();
      } catch (error: any) {
        console.error("Error starting service:", error);
        workflowStatus.value = "failed";
        statusMessage.value =
          error.message || "Failed to create payment. Please try again.";
      }
    };

    const startPolling = () => {
      stopPolling(); // Clear any existing polling
      pollingInterval.value = setInterval(pollServiceStatus, POLL_INTERVAL);
      // Poll immediately
      pollServiceStatus();
    };

    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
      }
      activePollController.value?.abort();
      activePollController.value = null;
    };

    const pollServiceStatus = async () => {
      if (!invocationId.value || !isOnline.value || pollingInProgress.value) {
        return;
      }

      // Check timeout
      if (Date.now() - startTime.value > MAX_POLL_TIME) {
        workflowStatus.value = "failed";
        statusMessage.value = "Transaction timeout. Payment expired.";
        stopPolling();
        return;
      }

      const controller = new AbortController();

      try {
        pollingInProgress.value = true;
        activePollController.value?.abort();
        activePollController.value = controller;

        // Poll workflow status to get paymentId, paymentUrl, and workflow state.
        // This keeps UI completion aligned with the durable workflow, which only
        // reaches "completed" after order processing has finished.
        const serviceStatus = await api.getOnlinePaymentServiceStatus(
          invocationId.value,
          controller.signal,
        );

        if (activePollController.value !== controller) {
          return;
        }

        const output = serviceStatus.output || {};
        const wfStatus = (serviceStatus.status || output.status) as
          | TransactionStatus
          | "error"
          | "pending";

        // Update state from workflow output
        if (output.paymentUrl && !paymentUrl.value) {
          paymentUrl.value = output.paymentUrl;
        }
        if (output.paymentId && !paymentId.value) {
          paymentId.value = output.paymentId;
        }

        // Map workflow status to our status
        if (wfStatus === "error") {
          workflowStatus.value = "failed";
          statusMessage.value =
            output.error || serviceStatus.error || "Payment failed";
          stopPolling();
        } else if (wfStatus === "pending") {
          // Keep current status, workflow is still initializing
          return;
        } else if (
          wfStatus === "initializing" ||
          wfStatus === "payment_created" ||
          wfStatus === "waiting_for_payment" ||
          wfStatus === "payment_confirmed" ||
          wfStatus === "processing_payment"
        ) {
          workflowStatus.value = wfStatus;
        } else if (wfStatus === "completed") {
          workflowStatus.value = wfStatus;
          stopPolling();
          if (paymentId.value) {
            props.onConfirm(props.totalAmount, paymentId.value);
          }
          setTimeout(() => {
            props.onClose();
          }, 1500);
        } else if (wfStatus === "failed") {
          workflowStatus.value = wfStatus;
          stopPolling();
          if (output.error) {
            statusMessage.value = output.error;
          }
        }
      } catch (error: any) {
        if (error?.name === "AbortError") {
          return;
        }
        console.error("Error polling service status:", error);
        // Don't stop polling on network errors
        // The service might still be processing
      } finally {
        if (activePollController.value === controller) {
          activePollController.value = null;
        }
        pollingInProgress.value = false;
      }
    };

    const isProduction = import.meta.env.VITE_ENV === "production";

    return () => {
      return (
        <LemmonModal
          title="ONLINE PAYMENT (V3)"
          onClose={() => props.onClose()}
          hideClose={
            displayStatus.value === "processing" ||
            displayStatus.value === "success"
          }
          renderButtons={() => (
            <div class="p-4 border-t ">
              <div class="flex justify-between gap-2">
                <button
                  onClick={() => props.onClose()}
                  class="btn btn-neutral"
                  type="button"
                  disabled={
                    displayStatus.value === "processing" ||
                    displayStatus.value === "success"
                  }
                >
                  Close
                </button>
              </div>
            </div>
          )}
        >
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-center p-4">
            {/* Payment URL or Loading */}
            <div class="flex flex-col items-center justify-center">
              {displayStatus.value === "initializing" ||
              displayStatus.value === "processing" ? (
                <div class="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
              ) : paymentUrl.value ? (
                <>
                  <QRCodeComponent url={paymentUrl.value} />
                  {isProduction ? null : (
                    <div class="text-center py-2">
                      <a
                        href={paymentUrl.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-primary hover:text-primary-focus underline break-all text-sm"
                      >
                        {paymentUrl.value}
                      </a>
                    </div>
                  )}
                </>
              ) : (
                <div class="text-center ">
                  {displayStatus.value === "error" ? (
                    <span class="text-red-500">Payment URL unavailable</span>
                  ) : (
                    <div class="animate-pulse">Loading payment link...</div>
                  )}
                </div>
              )}
            </div>

            {/* Payment Info */}
            <div class="flex flex-col items-center gap-4 text-center">
              <div class="text-2xl text-accent">
                {formatPrice(
                  props.totalAmount,
                  true,
                  2,
                  auth.user.value?.restaurant.currency || "CHF",
                )}
              </div>

              {displayStatus.value === "waiting" ? (
                <>
                  <span class="loading loading-spinner loading-lg text-primary" />
                  <div class="">{statusMessage.value}</div>
                </>
              ) : displayStatus.value === "offline" ? (
                <>
                  <div class="text-warning">
                    <span class="material-symbols-outlined text-6xl">
                      cloud_off
                    </span>
                  </div>
                  <div class="text-warning text-lg">{statusMessage.value}</div>
                </>
              ) : displayStatus.value === "processing" ? (
                <>
                  <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  <div class="text-blue-400">{statusMessage.value}</div>
                </>
              ) : displayStatus.value === "success" ? (
                <div class="text-success text-2xl">✅ Payment Successful!</div>
              ) : displayStatus.value === "error" ? (
                <div class="text-error text-2xl">❌ {statusMessage.value}</div>
              ) : (
                <div class="">{statusMessage.value}</div>
              )}
            </div>
          </div>
        </LemmonModal>
      );
    };
  },
});
